import { Formik, getIn, useFormikContext } from 'formik'
import { ReactElement } from 'react'
import { useIntl, FormattedMessage, defineMessages, MessageDescriptor } from 'react-intl'
import { NavLink } from 'react-router-dom'
import Card from '@10xLabs/react-web/components/Card'
import { Column } from '@10xLabs/react-web/components/Column'
import { Row } from '@10xLabs/react-web/components/Row'
import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Label } from 'components/v2/Label'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { ISalesChannel } from 'features/Services/components/NewServicesV2/components/OfferSettingsCard/types'
import { SelectSalesChannelsV2 } from 'features/Services/components/SelectSalesChannelsV2'
import { GTCitySuggestion, GTTCityAutocomplete } from 'gql/types.generated'
import { getUnprocessableErrors } from 'utils/error'
import { IAncillaryFormProps, INewPreferredSeatsAncillaryForm, INewAncillaryProps } from './types'
import { schema } from './schema'
import { SelectCountries, useCountry } from 'components/SelectCountries'
import { BothWay, OneWay, SelectSegmentsForm, ToggleIsReturn } from 'components/SelectSegments'
import Heading from 'components/v2/Heading'
import { SplitColumn } from 'components/SelectOriginCityAndDestinationCity'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'
import {
  SelectRouteServices,
  getValidRouteServicesBySegments
} from 'features/FareRules/components/NewFareRule/SelectRouteServices'
import { PreferredSeatsDeckPriceSettings } from 'features/Ancillaries/components/PreferredSeatsDeckPriceSettings'
import styled from 'styled-components'
import { isEmpty } from 'lodash'
import { canSelectCompany } from 'features/Login/utils'
import { useLogin } from 'features/Login/hooks'
import { useAllSegmentsByCities } from 'features/Segments/hooks'
import { mapSegmentSuggestionsToFormSegments } from 'features/Segments/utils'
import { IFormSegment } from 'components/SelectSegments/types'
import { CompanySuggestion } from 'gql/generated/graphql'
const StyledColumn = styled(Column)`
  border: 1px solid transparent;
`
const messages: Record<string, MessageDescriptor> = defineMessages({
  salesChannelsPlaceholder: {
    id: 'ancillaries.form.salesChannels.placeholder',
    defaultMessage: 'Select a sales channel'
  },
  companyLabel: {
    id: 'ancillaries.form.company.label',
    defaultMessage: 'Company'
  },
  companyPlaceholder: {
    id: 'ancillaries.form.company.placeholder',
    defaultMessage: 'Select company'
  },
  originPlaceholder: {
    id: 'preferredSeatsAncillary.form.origin.placeholder',
    defaultMessage: 'Select an origin'
  },
  destinationPlaceholder: {
    id: 'preferredSeatsAncillary.form.destination.placeholder',
    defaultMessage: 'Select a destination'
  }
})
const Form = ({ error, loading, isEdit }: IAncillaryFormProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const { values, touched, errors, setFieldValue, handleSubmit, handleBlur, setFieldTouched } =
    useFormikContext<INewPreferredSeatsAncillaryForm>()
  const country = useCountry(values.countryCode)
  const { segmentSuggestions } = useAllSegmentsByCities({
    companyId: values.company?.id,
    cityOrigins: [values.origin],
    cityDestinations: [values.destination],
    isBothWays: values.isReturn
  })
  const segmentsOptions = mapSegmentSuggestionsToFormSegments(segmentSuggestions) as IFormSegment[]

  const currencyCode = !isEmpty(values.countryCode) ? (values.countryCode === 'MX' ? ' MXN' : ' USD') : undefined
  const handleSalesChannelsChange = (salesChannels: ISalesChannel[]): void => {
    setFieldValue('salesChannels', salesChannels)
  }
  const resetSegments = (): void => {
    setFieldValue('allSegmentsAreSelected', false)
    setFieldValue('segments', [])
    setFieldValue('segmentsOrigins', [])
    setFieldValue('segmentsDestinations', [])
  }

  const resetServices = (): void => {
    setFieldValue('allServicesAreSelected', false)
    setFieldValue('routeServices', [])
  }

  const handleCityChange = (key: 'origin' | 'destination', value: GTTCityAutocomplete): void => {
    setFieldValue(key, value)
    setFieldTouched(key, true)

    resetSegments()
    resetServices()
  }

  const handleOriginChange = (value: GTCitySuggestion): void => {
    handleCityChange('origin', {
      id: value.id,
      name: value.name,
      stateCode: value.stateCode || ''
    })

    if (values.destination.id === value.id) {
      setFieldValue('isReturn', false)
    }
  }

  const handleDestinationChange = (value: GTCitySuggestion): void => {
    handleCityChange('destination', {
      id: value.id,
      name: value.name,
      stateCode: value.stateCode || ''
    })

    if (values.origin.id === value.id) {
      setFieldValue('isReturn', false)
    }
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Card.Content noHeader>
        <Row>
          <Column>
            <Heading.H5>
              {isEdit ? (
                <FormattedMessage
                  id="preferredSeatsAncillary.edit.preferred-seats.title"
                  defaultMessage="Edit Preferred seats"
                />
              ) : (
                <FormattedMessage
                  id="preferredSeatsAncillary.add.preferred-seats.title"
                  defaultMessage="Add Preferred seats"
                />
              )}
            </Heading.H5>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <SelectCountries
              value={values.countryCode}
              onChange={(value): void => {
                setFieldValue('countryCode', value)
              }}
              onBlur={handleBlur}
              touched={getIn(touched, 'countryCode')}
              errors={getIn(errors, 'countryCode')}
              disabled={isEdit}
            />
          </Column>
        </Row>
        {canSelectCompany(login.accessToken) ? (
          <Row>
            <Column width="50%">
              <CompanyAutocomplete
                id="company"
                label={intl.formatMessage(messages.companyLabel)}
                placeholder={intl.formatMessage(messages.companyPlaceholder)}
                value={values.company.name}
                onChange={(value: CompanySuggestion): void => {
                  setFieldValue('company', value)
                }}
                touched={getIn(touched, 'company.id')}
                errors={getIn(errors, 'company.id')}
                disabled={isEdit}
              />
            </Column>
          </Row>
        ) : null}
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage
                id="preferredSeatsAncillary.new.selectServices.title"
                defaultMessage="Select services"
              />
            </Heading.H5>
          </Column>
        </Row>
        <div>
          <Label>
            <FormattedMessage id="preferredSeatsAncillary.new.cities.label" defaultMessage="Route" />
          </Label>
        </div>
        <Row>
          <Column>
            <CityAutocomplete
              id="origin.id"
              value={values.origin.name}
              onChange={handleOriginChange}
              onBlur={handleBlur}
              placeholder={intl.formatMessage(messages.originPlaceholder)}
              touched={getIn(touched, 'origin.id')}
              errors={getIn(errors, 'origin.id')}
            />
          </Column>
          <SplitColumn>
            <ToggleIsReturn setFieldValue={setFieldValue} values={values}>
              {values.isReturn ? <BothWay /> : <OneWay />}
            </ToggleIsReturn>
          </SplitColumn>
          <Column>
            <CityAutocomplete
              id="destination.id"
              value={values.destination.name}
              onChange={handleDestinationChange}
              onBlur={handleBlur}
              placeholder={intl.formatMessage(messages.destinationPlaceholder)}
              touched={getIn(touched, 'destination.id')}
              errors={getIn(errors, 'destination.id')}
            />
          </Column>
        </Row>
        <Row>
          <StyledColumn>
            <div>
              <Label>
                <FormattedMessage id="preferredSeatsAncillary.new.segments.label" defaultMessage="Segments" />
              </Label>
            </div>
            <div>
              <SelectSegmentsForm
                segments={segmentsOptions}
                values={values}
                setFieldValue={setFieldValue}
                isReturn={values.isReturn}
                segmentsOrigins={values.segmentsOrigins}
                setSegmentsOrigins={(segmentsOrigins): void => {
                  setFieldValue('segmentsOrigins', segmentsOrigins)
                }}
                segmentsDestinations={values.segmentsDestinations}
                setSegmentsDestinations={(segmentsDestinations): void => {
                  setFieldValue('segmentsDestinations', segmentsDestinations)
                }}
                setSegments={(segments): void => {
                  setFieldValue('segments', segments)
                  setFieldValue('allServicesAreSelected', false)
                  setFieldValue(
                    'routeServices',
                    getValidRouteServicesBySegments({
                      segments,
                      routeServices: values.routeServices
                    })
                  )
                }}
                allSegmentsAreSelected={values.allSegmentsAreSelected}
                setAllSegmentsAreSelected={(allSegmentsAreSelected): void => {
                  setFieldValue('allSegmentsAreSelected', allSegmentsAreSelected)
                }}
                isShowingAllSegmentsCheckbox
                touched={touched}
                errors={errors}
                disabled={values.allSegmentsAreSelected}
                companyId={values.company.id || undefined}
              />
            </div>
          </StyledColumn>
        </Row>
        <div>
          <Label>
            <FormattedMessage id="preferredSeatsAncillary.new.routeServices.label" defaultMessage="Services" />
          </Label>
        </div>
        <SelectRouteServices
          values={values.routeServices}
          setValues={(routeServices: void): void => {
            setFieldValue('routeServices', routeServices)
          }}
          segments={values.segments}
          cityOrigin={values.origin}
          cityDestination={values.destination}
          isReturn={values.isReturn}
          touched={touched && touched.routeServices}
          errors={errors && errors.routeServices}
          isShowingAllServicesCheckbox
          allServicesAreSelected={values.allServicesAreSelected}
          setAllServicesAreSelected={(allServicesAreSelected: void): void => {
            setFieldValue('allServicesAreSelected', allServicesAreSelected)
          }}
        />
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="preferredSeatsAncillary.form.salesChannels.label" defaultMessage="Sales Channels" />
            </Label>
            <SelectSalesChannelsV2
              id="salesChannels"
              values={values.salesChannels}
              placeholder={intl.formatMessage(messages.salesChannelsPlaceholder)}
              onChange={handleSalesChannelsChange}
              touched={getIn(touched, 'salesChannels')}
              errors={getIn(errors, 'salesChannels')}
              disabled={isEmpty(values.countryCode)}
              countryId={country?.id}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <PreferredSeatsDeckPriceSettings
              title={<FormattedMessage id="preferredSeatsAncillary.form.singleDeck" defaultMessage="Standar Bus" />}
              value={values.singleDeckPrices}
              currencyCode={currencyCode}
              onChange={(value): void => {
                setFieldValue('singleDeckPrices', value)
              }}
              touched={getIn(touched, 'singleDeckPrices')}
              errors={getIn(errors, 'singleDeckPrices')}
            />
          </Column>
          <Column width="50%">
            <PreferredSeatsDeckPriceSettings
              title={<FormattedMessage id="preferredSeatsAncillary.form.doubleDeck" defaultMessage="Double Decker" />}
              value={values.doubleDeckPrices}
              currencyCode={currencyCode}
              onChange={(value): void => {
                setFieldValue('doubleDeckPrices', value)
              }}
              touched={getIn(touched, 'doubleDeckPrices')}
              errors={getIn(errors, 'doubleDeckPrices')}
            />
          </Column>
        </Row>
      </Card.Content>
      <Card.Footer>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              {isEdit ? (
                <FormattedMessage
                  id="ancillaries.form.errorUpdate"
                  defaultMessage="There was an error updating the ancillary"
                />
              ) : (
                <FormattedMessage
                  id="ancillaries.form.errorCreate"
                  defaultMessage="There was an error creating the ancillary"
                />
              )}
            </ErrorMessage>
          </div>
        )}
        <Card.FooterActions>
          <Button as={NavLink} to="/ancillaries/preferred-seats" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </Card.FooterActions>
      </Card.Footer>
    </form>
  )
}

export const NewPreferredSeatsAncillaryForm = ({
  onSubmit,
  error,
  loading,
  isEditView,
  ancillary
}: INewAncillaryProps): ReactElement => {
  const intl = useIntl()

  const defaultValues: INewPreferredSeatsAncillaryForm = {
    allSegmentsAreSelected: false,
    allServicesAreSelected: false,
    company: { id: '', name: '' },
    countryCode: '',
    destination: { id: '', name: '', code: '' },
    isReturn: false,
    origin: { id: '', name: '', code: '' },
    routeServices: [],
    salesChannels: [],
    segments: [],
    segmentsDestinations: [],
    segmentsOrigins: [],
    singleDeckPrices: [],
    doubleDeckPrices: []
  }

  const initialValues = ancillary ?? defaultValues

  return (
    <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(): ReactElement => {
        return <Form error={error} loading={loading} isEdit={isEditView} />
      }}
    </Formik>
  )
}
